import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'next-i18next';
import { RawRESTClient } from 'src/apis/service';
import { PageService } from 'src/apis/service/PageService';
import ButtonBase from 'src/components/GenericButton';
import ModalLayoutBase, { ModalContentStyle, ModalHeader, ModalLayoutProps, ModalFooter } from 'src/components/ModalLayout';
import { useInventoryItem } from 'src/hooks/useInventoryItem';
import useLogin from 'src/hooks/useLogin';
import useMount from 'src/hooks/useMount';
import { EVENT_NAME, EVENT_TYPE } from 'src/types/Event';
import { pushEvent } from 'src/utils/event-gateway';
import { isSubscriptionPageExtra, toSubscriptionBenefitModules, toSubscriptionHeaderModules } from 'src/utils/shop';
import Badge from './Badge';
const slideInTop = keyframes`
  0% {
    transform: translateY(100%);
  }
  100% {
    transform: translateY(0%);
  }
`;
const ModalLayout = styled(ModalLayoutBase)`
  overflow: hidden;

  ${({
  theme
}) => theme.screenSize.mobile} {
    animation: ${slideInTop} 0.4s ease-in-out;
  }
`;
const ModalContent = styled(ModalContentStyle)`
  padding: 18px 24px 0;
  z-index: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
`;
const ModalGradient = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 0;
  background: ${({
  theme
}) => `radial-gradient(68.92% 50% at 49.87% 0%, rgba(116, 98, 9, 0.90) 0%, ${theme.color.surface2} 100%), ${theme.color.surface2}`};
`;
const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;

  padding: 20px;
  border-radius: 16px;

  background-color: ${({
  theme
}) => theme.color.surfaceDeep};
  box-shadow: 0 2px 20px 0 rgba(0, 0, 0, 0.13);
`;
const Logo = styled.img`
  margin-block-end: 24px;

  flex-shrink: 0;

  object-fit: contain;

  height: 52px;
`;
const Title = styled.div`
  margin-block-end: 20px;

  color: ${({
  theme
}) => theme.color.contentDeep};

  font-size: 20px;
  font-weight: 700;
  text-align: center;
`;
const Row = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  gap: 12px;
  flex-direction: column;
  text-align: center;

  & + & {
    padding-top: 12px;
    border-top: 1px solid ${({
  theme
}) => theme.color.content12};
  }
`;
const RowText = styled.div`
  display: flex;
  gap: 4px;
  flex-direction: column;
  align-items: flex-start;
  flex-grow: 1;
`;
const RowIcon = styled.img`
  flex-shrink: 0;

  width: 44px;
  height: 44px;
`;
const RowTitle = styled.div`
  display: flex;
  flex-grow: 1;
  gap: 5px;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;
const RowTitleContent = styled.div`
  flex-grow: 1;

  display: flex;

  color: ${({
  theme
}) => theme.color.contentDeepFixed};

  font-size: 15px;
  font-weight: 700;
`;
const RowDesc = styled.div`
  color: ${({
  theme
}) => theme.color.content8};

  font-size: 13px;
  font-weight: 400;
`;
const Button = styled(ButtonBase)`
  margin-top: 12px;
  margin-bottom: 10px;

  width: 100%;

  flex-direction: column;
  gap: 2px;
`;
const ButtonSubtitle = styled.span`
  font-size: 12px;
  font-weight: 400;
`;
interface Props extends ModalLayoutProps {
  referrer: 'mirror_renew' | 'localefilter' | 'genderfilter' | 'mirror' | 'my_profile' | 'item_inventory' | 'no_item';
}
const SubscriptionModal = ({
  referrer,
  ...props
}: Props) => {
  const {
    t
  } = useTranslation();
  const {
    isLogin
  } = useLogin();
  const {
    data,
    isLoading
  } = useQuery({
    queryKey: ['subscriptionPages'],
    async queryFn() {
      const service = new PageService(new RawRESTClient());
      return service.getSubscriptionPage();
    },
    enabled: isLogin
  });
  const {
    itemId
  } = useInventoryItem(({
    categoryId
  }) => categoryId === 'VIVIDI_UNLIMITED') ?? {};
  const isSubscribed = !!itemId;
  useMount(() => {
    pushEvent({
      eventType: EVENT_TYPE.SHOP,
      eventName: EVENT_NAME.SHOP__OPEN_SUB_INTRO,
      eventParams: {
        action_category: 'open',
        tab: 'shop',
        page: 'sub_intro',
        target: 'sub_intro',
        is_subscribed: isSubscribed,
        subscribed_product_id: itemId,
        referrer
      }
    });
  });
  if (data == null || data.pages.length === 0 || isLoading) {
    return null;
  }
  const [page] = data.pages;
  const [subHeaderModule] = toSubscriptionHeaderModules(page);
  const benefits = toSubscriptionBenefitModules(page);
  const handleClickPurchase = () => {
    pushEvent({
      eventType: EVENT_TYPE.SHOP,
      eventName: EVENT_NAME.SHOP__CLICK_SUB_START,
      eventParams: {
        action_category: 'click',
        tab: 'shop',
        page: 'sub_intro',
        target: 'sub_start'
      }
    });

    /*
    showModal({
      key: ModalType.SUBSCRIPTION_PURCHASE_POPUP,
      component: () => <Purchase page={page} />,
    });
    */
    window.location.href = 'https://vividiapp.onelink.me/EyZ5/6f51808r';
  };
  if (!isSubscriptionPageExtra(page)) return null;
  const {
    backgroundColor
  } = page;
  return <ModalLayout {...props} data-testid='subscription-modal' isDim style={{
    backgroundColor
  }} data-sentry-element="ModalLayout" data-sentry-component="SubscriptionModal" data-sentry-source-file="index.tsx">
      <ModalGradient data-sentry-element="ModalGradient" data-sentry-source-file="index.tsx" />
      <ModalHeader data-sentry-element="ModalHeader" data-sentry-source-file="index.tsx" />
      <Badge data-sentry-element="Badge" data-sentry-source-file="index.tsx" />
      <ModalContent data-sentry-element="ModalContent" data-sentry-source-file="index.tsx">
        <Logo src={subHeaderModule?.titleImageUrl} alt={page.pageId} data-sentry-element="Logo" data-sentry-source-file="index.tsx" />
        <Title data-sentry-element="Title" data-sentry-source-file="index.tsx">{subHeaderModule?.description}</Title>
        <Wrap data-sentry-element="Wrap" data-sentry-source-file="index.tsx">
          {benefits.map(({
          moduleId,
          title,
          description,
          iconUrl,
          featureId
        }) => <Row key={moduleId}>
              <RowIcon src={iconUrl} alt={featureId} />
              <RowText>
                <RowTitle>
                  <RowTitleContent>{title}</RowTitleContent>
                </RowTitle>
                <RowDesc>{description}</RowDesc>
              </RowText>
            </Row>)}
        </Wrap>
      </ModalContent>
      <ModalFooter data-sentry-element="ModalFooter" data-sentry-source-file="index.tsx">
        <Button id='PURCHASE_SUBSCRIPTION' disabled={isSubscribed} onClick={handleClickPurchase} size='large' variant='primary' data-sentry-element="Button" data-sentry-source-file="index.tsx">
          <span>
            {isSubscribed ? t('vividi_subs_benefits_btn_title_subscribed') : t('vividi_subs_benefits_btn_title')}
          </span>
          <ButtonSubtitle data-sentry-element="ButtonSubtitle" data-sentry-source-file="index.tsx">
            {isSubscribed ? t('vividi_subs_benefits_btn_sub_subscribed') : t('BUTTON__DOWNLOAD_APP')}
          </ButtonSubtitle>
        </Button>
      </ModalFooter>
    </ModalLayout>;
};
export default SubscriptionModal;